/**
 * QR 스캔 엔진 — 카메라 획득 · 디코딩 · 토치 (모바일 스캐너 공용 SSoT)
 *
 * 현장 보고(2026-11-04): 협력사 QR 스캔이 흐려 인식이 안 된다.
 * 원인이 화면마다 흩어져 있어 한 곳으로 모은다 —
 *   ① 연속 초점(focusMode)을 요청하지 않아 기기 기본 초점에 맡겨져 있었다
 *      (일부 안드로이드는 고정 초점이거나 한 번만 AF 하고 멈춘다 → 근접 촬영에서 바로 흐려진다)
 *   ② 해상도 제약이 없어 640x480 으로 떨어질 수 있었다
 *   ③ 네이티브 BarcodeDetector(흐리거나 기울어진 코드에 jsQR 보다 강하다)를
 *      정작 현장 진입 화면인 QR 로그인이 쓰지 않고 있었다
 *   ④ 전체 프레임을 jsQR 에 넘겨 모바일에서 처리가 무거웠다
 *
 * MobileQRLogin / QRScanner 양쪽이 이 파일을 쓴다 — 한쪽만 좋아지는 일이 없게.
 */
import jsQR from 'jsqr';
import { silentLog } from './errorLogger';

const IDEAL_WIDTH = 1280;
const IDEAL_HEIGHT = 720;
const MAX_DECODE_SIZE = 768;   // jsQR 에 넘기는 이미지의 최대 변 (이보다 크면 비용만 늘고 득이 없다)
const ROI_RATIO = 0.8;         // 화면 가이드 사각형을 덮는 중앙 정사각형 비율
const FULL_FRAME_AFTER = 5;    // ROI 로 연속 N 회 실패하면 한 번은 전체 프레임도 본다 (가이드 밖 안전망)

/** 토치(플래시) 지원 여부. */
export function supportsTorch(track) {
  try {
    return track?.getCapabilities?.().torch === true;
  } catch {
    return false;
  }
}

/** 연속 자동초점 요청. 지원하지 않는 기기에서는 조용히 false. */
export async function applyContinuousFocus(track) {
  if (!track?.applyConstraints) return false;
  let caps;
  try {
    caps = track.getCapabilities?.() || {};
  } catch {
    return false;
  }
  // focusMode 자체를 노출하지 않는 기기가 많다 — 그런 곳에 constraint 를 밀어넣지 않는다
  if (!Array.isArray(caps.focusMode) || !caps.focusMode.includes('continuous')) return false;
  try {
    await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
    return true;
  } catch (e) {
    silentLog('qrScanEngine.applyContinuousFocus', e);
    return false;
  }
}

/** 토치 on/off. 성공 여부를 돌려준다. */
export async function setTorch(track, on) {
  if (!supportsTorch(track)) return false;
  try {
    await track.applyConstraints({ advanced: [{ torch: !!on }] });
    return true;
  } catch (e) {
    silentLog('qrScanEngine.setTorch', e);
    return false;
  }
}

/**
 * 후면 카메라 스트림을 연다.
 * 해상도는 ideal 로만 요청하고, 그래도 거부하는 기기가 있으면 제약 없이 한 번 더 시도한다.
 * 권한 거부(NotAllowedError) 등은 호출부가 메시지를 구분해야 하므로 그대로 던진다.
 */
export async function openQrCamera() {
  const base = { facingMode: { ideal: 'environment' } };
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { ...base, width: { ideal: IDEAL_WIDTH }, height: { ideal: IDEAL_HEIGHT } },
      audio: false,
    });
  } catch (e) {
    if (e?.name !== 'OverconstrainedError' && e?.name !== 'ConstraintNotSatisfiedError') throw e;
    stream = await navigator.mediaDevices.getUserMedia({ video: base, audio: false });
  }
  const track = stream.getVideoTracks()[0] || null;
  await applyContinuousFocus(track);
  return { stream, track, torchSupported: supportsTorch(track) };
}

/**
 * 디코더 하나를 만든다. 반환된 decode(video, canvas) 는 찾은 문자열 또는 null.
 * BarcodeDetector 인스턴스와 ROI 실패 카운터를 클로저에 들고 있으므로 스캐너당 1개만 만든다.
 */
export function createQrDecoder() {
  let detector = null;
  let detectorBroken = false;
  let roiMisses = 0;

  const getDetector = () => {
    if (detectorBroken || typeof window === 'undefined' || !('BarcodeDetector' in window)) return null;
    if (!detector) {
      try {
        detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      } catch {
        detectorBroken = true;
        return null;
      }
    }
    return detector;
  };

  return async function decode(video, canvas) {
    if (!video || !canvas || video.readyState < 2 || !video.videoWidth) return null;

    // 1차: 네이티브 BarcodeDetector — 전체 프레임, 흐린 코드에 강하다
    const d = getDetector();
    if (d) {
      try {
        const found = await d.detect(video);
        if (found?.length) return found[0].rawValue || null;
      } catch (e) {
        detectorBroken = true;   // 한 번 실패한 기기에서는 계속 실패한다 → 이후 jsQR 만
        silentLog('qrScanEngine.barcodeDetector', e);
      }
    }

    // 2차: jsQR — 가운데만 잘라서 넘긴다 (전체 프레임은 모바일에서 너무 무겁다)
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const useFull = roiMisses >= FULL_FRAME_AFTER;
    const side = Math.round(Math.min(vw, vh) * ROI_RATIO);
    const sw = useFull ? vw : side;
    const sh = useFull ? vh : side;
    const sx = useFull ? 0 : Math.round((vw - side) / 2);
    const sy = useFull ? 0 : Math.round((vh - side) / 2);

    const scale = Math.min(1, MAX_DECODE_SIZE / Math.max(sw, sh));
    const dw = Math.max(1, Math.round(sw * scale));
    const dh = Math.max(1, Math.round(sh * scale));

    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, dw, dh);

    const img = ctx.getImageData(0, 0, dw, dh);
    const res = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
    if (res?.data) {
      roiMisses = 0;
      return res.data;
    }
    roiMisses = useFull ? 0 : roiMisses + 1;
    return null;
  };
}
